/**
 * Handles boutique-only (`boutique_only` tag) product restrictions on the storefront.
 * Disables Wishlist Hero add-to-cart actions and blocks cart/add for tagged products.
 */
(() => {
  const BOUTIQUE_TAG = 'boutique_only';
  const PRODUCT_CACHE = new Map();
  const BOUTIQUE_VARIANT_IDS = new Set();

  /**
   * Returns the boutique exclusive label from theme globals.
   * @returns {string}
   */
  function getBoutiqueLabel() {
    return (
      window.variantStrings?.boutiqueExclusive ||
      window.ShopifyStorefront?.utils?.textBadgeBoutiqueExclusive ||
      'Boutique Exclusive'
    );
  }

  /**
   * Registers boutique variant ids so cart/add can be blocked.
   * @param {object|null} product
   */
  function registerBoutiqueVariants(product) {
    if (!isBoutiqueProduct(product)) return;
    (product.variants || []).forEach((variant) => {
      if (variant?.id != null) BOUTIQUE_VARIANT_IDS.add(String(variant.id));
    });
  }

  /**
   * Extracts a product handle from a product URL.
   * @param {string} url
   * @returns {string|null}
   */
  function getHandleFromUrl(url) {
    if (!url) return null;
    try {
      const pathname = new URL(url, window.location.origin).pathname;
      const match = pathname.match(/\/products\/([^/?#]+)/i);
      return match ? decodeURIComponent(match[1]) : null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Fetches product JSON and caches the result.
   * @param {string} handle
   * @returns {Promise<object|null>}
   */
  async function fetchProduct(handle) {
    if (!handle) return null;
    if (PRODUCT_CACHE.has(handle)) return PRODUCT_CACHE.get(handle);

    const request = fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`)
      .then((response) => {
        if (!response.ok) throw new Error('Product fetch failed');
        return response.json();
      })
      .then((product) => {
        registerBoutiqueVariants(product);
        return product;
      })
      .catch(() => null);

    PRODUCT_CACHE.set(handle, request);
    return request;
  }

  /**
   * Checks whether a product has the boutique_only tag.
   * @param {object|null} product
   * @returns {boolean}
   */
  function isBoutiqueProduct(product) {
    if (!product?.tags) return false;
    const tags = Array.isArray(product.tags)
      ? product.tags
      : String(product.tags)
          .split(',')
          .map((tag) => tag.trim());
    return tags.some((tag) => String(tag).trim() === BOUTIQUE_TAG);
  }

  /**
   * Seeds known boutique variant ids from Liquid-rendered markup.
   */
  function seedBoutiqueVariantsFromDom() {
    document.querySelectorAll('[data-boutique-variant-ids]').forEach((node) => {
      try {
        const ids = JSON.parse(node.textContent || '[]');
        if (Array.isArray(ids)) {
          ids.forEach((id) => BOUTIQUE_VARIANT_IDS.add(String(id)));
        }
      } catch (error) {
        // Ignore invalid JSON payloads.
      }
    });

    document.querySelectorAll('[data-boutique-only="true"]').forEach((node) => {
      const variantInput = node.querySelector('[name="id"], .product-variant-id');
      if (variantInput?.value) BOUTIQUE_VARIANT_IDS.add(String(variantInput.value));

      const wlhVariant = node.getAttribute('data-wlh-variantid');
      if (wlhVariant) BOUTIQUE_VARIANT_IDS.add(String(wlhVariant));

      node.querySelectorAll('[data-wlh-variantid]').forEach((el) => {
        const id = el.getAttribute('data-wlh-variantid');
        if (id) BOUTIQUE_VARIANT_IDS.add(String(id));
      });
    });
  }

  /**
   * Disables a Wishlist Hero add-to-cart control.
   * @param {HTMLElement} button
   */
  function disableWishlistAddToCart(button) {
    if (!button || button.dataset.boutiqueOnlyApplied === 'true') return;

    button.setAttribute('disabled', 'disabled');
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('boutique-only-disabled');
    button.style.pointerEvents = 'none';
    button.style.opacity = '0.5';
    button.dataset.boutiqueOnlyApplied = 'true';

    const label = getBoutiqueLabel();
    if (button.getAttribute('aria-label')) {
      button.setAttribute('aria-label', label);
    }

    const labelNode = button.querySelector('.MuiButton-label, span');
    if (labelNode && labelNode.childElementCount === 0) {
      labelNode.textContent = label;
    }

    button.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  }

  /**
   * Processes Wishlist Hero cards and disables ATC for boutique-only products.
   * @param {ParentNode} [root=document]
   * @returns {Promise<void>}
   */
  async function processWishlistCards(root = document) {
    const cards = root.querySelectorAll(
      '.wishlist-hero-list-card, [class*="wishlist-hero-list-card"]'
    );

    await Promise.all(
      Array.from(cards).map(async (card) => {
        if (card.dataset.boutiqueChecked === 'true') return;

        const productLink = card.querySelector('a[href*="/products/"]');
        const handle = getHandleFromUrl(productLink?.getAttribute('href'));
        if (!handle) return;

        card.dataset.boutiqueChecked = 'true';

        const product = await fetchProduct(handle);
        if (!isBoutiqueProduct(product)) return;

        card.setAttribute('data-boutique-only', 'true');

        const addButtons = card.querySelectorAll(
          '.wishlist-hero-list-card-footer-add-to-cart, [class*="add-to-cart"], button[aria-label*="ADD" i], button[aria-label*="CART" i]'
        );
        addButtons.forEach(disableWishlistAddToCart);

        if (!card.querySelector('.nug-boutique-exclusive-badge')) {
          const badge = document.createElement('span');
          badge.className = 'nug-badge nug-boutique-exclusive-badge';
          badge.textContent = getBoutiqueLabel();
          badge.style.cssText =
            'display:inline-block;margin:0.5rem 0;padding:7px 8px;font-size:11px;text-transform:uppercase;border:1px solid #000;max-width: min-content;';
          const header = card.querySelector('.wishlist-hero-list-card-header, .MuiCardHeader-root');
          (header || card).append(badge);
        }
      })
    );
  }

  /**
   * Observes DOM mutations to handle dynamically rendered wishlist content.
   */
  function observeWishlist() {
    const observer = new MutationObserver((mutations) => {
      const shouldProcess = mutations.some((mutation) => mutation.addedNodes.length > 0);
        if (shouldProcess) {
            processWishlistCards();
        }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });

    processWishlistCards();
  }

  /**
   * Helper per estrarre gli items da un corpo di richiesta
   * @param {any} body - Il body della richiesta (string, FormData, object)
   * @returns {Array<{id: string, quantity: number}>}
   */
  function extractItemsFromRequestBody(body) {
    if (!body) return [];
  
    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body);
        return extractItemsFromParsedBody(parsed);
      } catch (_) {
        return extractItemsFromURLSearchParams(body);
      }
    }
  
    if (body instanceof FormData) {
      return extractItemsFromFormData(body);
    }
  
    if (body && typeof body === 'object') {
      return extractItemsFromParsedBody(body);
    }
  
    return [];
  }
  
  /**
   * Estrae items da un oggetto parsato
   */
  function extractItemsFromParsedBody(parsed) {
    if (!parsed) return [];
    
    if (parsed.id) {
      return [{ id: String(parsed.id), quantity: parseInt(parsed.quantity, 10) || 1 }];
    }
    
    if (parsed.items && Array.isArray(parsed.items)) {
      return parsed.items.map(item => ({
        id: String(item.id),
        quantity: parseInt(item.quantity, 10) || 1
      }));
    }
    
    return [];
  }
  
  /**
   * Estrae items da URLSearchParams (query string)
   */
  function extractItemsFromURLSearchParams(body) {
    const params = new URLSearchParams(body);
    const items = [];
    
    const id = params.get('id');
    if (id) {
      return [{ id: String(id), quantity: parseInt(params.get('quantity') || 1, 10) }];
    }
    
    let i = 0;
    while (params.has(`items[${i}][id]`)) {
      const id = params.get(`items[${i}][id]`);
      const qty = parseInt(params.get(`items[${i}][quantity]`) || 1, 10);
      if (id) {
        items.push({ id: String(id), quantity: qty });
      }
      i++;
    }
    
    return items;
  }
  
  /**
   * Estrae items da FormData
   */
  function extractItemsFromFormData(formData) {
    const items = [];
    
    const ids = formData.getAll('id');
    if (ids.length) {
      return ids.map(id => ({ id: String(id), quantity: 1 }));
    }
    
    let i = 0;
    while (formData.has(`items[${i}][id]`)) {
      const id = formData.get(`items[${i}][id]`);
      const qty = parseInt(formData.get(`items[${i}][quantity]`) || 1, 10);
      if (id) {
        items.push({ id: String(id), quantity: qty });
      }
      i++;
    }
    
    return items;
  }
  
  /**
   * Intercepts cart/add requests for known boutique-only variants.
   * @param {RequestInfo|URL} input
   * @param {RequestInit} [init]
   * @returns {Promise<Response>}
   */
  async function guardedFetch(input, init = {}) {
    const url = typeof input === 'string' ? input : input?.url || '';
    const isCartAdd = /\/cart\/add(\.js)?(\?|$)/.test(url);
  
    if (!isCartAdd) {
      return window.__boutiqueOriginalFetch(input, init);
    }
  
    try {
      const items = extractItemsFromRequestBody(init.body);
      
      if (items.length === 0) {
        return window.__boutiqueOriginalFetch(input, init);
      }
  
      const allowedItems = items.filter(item => !BOUTIQUE_VARIANT_IDS.has(item.id));
  
        if (allowedItems.length === 0) {
        return new Response(
          JSON.stringify({ status: 200, message: 'No items to add' }),
          {
            status: 200,
            headers: { 'Content-Type': 'application/json' }
          }
        );
      }
  
      let newBody;
      if (allowedItems.length === 1) {
        newBody = JSON.stringify({
          id: allowedItems[0].id,
          quantity: allowedItems[0].quantity
        });
      } else {
        newBody = JSON.stringify({ items: allowedItems });
      }
  
      const newInit = {
        ...init,
        body: newBody,
        headers: {
          ...init.headers,
          'Content-Type': 'application/json'
        }
      };
  
      return window.__boutiqueOriginalFetch(input, newInit);
    } catch (error) {
      console.warn('Boutique guard fallback:', error);
      return window.__boutiqueOriginalFetch(input, init);
    }
  }

    /**
     * Intercetta le richieste XMLHttpRequest per /cart/add.js
     * e filtra i prodotti boutique-only.
     */
    function installXHRInterceptor() {
      if (window.__boutiqueXHRInstalled) return;
      window.__boutiqueXHRInstalled = true;
    
      const originalOpen = XMLHttpRequest.prototype.open;
      const originalSend = XMLHttpRequest.prototype.send;
    
      function extractItemsFromBody(body) {
        if (!body) return [];
    
        if (typeof body === 'string') {
          try {
            const parsed = JSON.parse(body);
            if (parsed.id) {
              return [{ id: String(parsed.id), quantity: parseInt(parsed.quantity, 10) || 1 }];
            }
            if (parsed.items && Array.isArray(parsed.items)) {
              return parsed.items.map(item => ({
                id: String(item.id),
                quantity: parseInt(item.quantity, 10) || 1
              }));
            }
            return [];
          } catch (_) {
            const params = new URLSearchParams(body);
            const id = params.get('id');
            if (id) {
              return [{ id: String(id), quantity: parseInt(params.get('quantity') || 1, 10) }];
            }
            const items = [];
            let i = 0;
            while (params.has(`items[${i}][id]`)) {
              const id = params.get(`items[${i}][id]`);
              const qty = parseInt(params.get(`items[${i}][quantity]`) || 1, 10);
              if (id) items.push({ id: String(id), quantity: qty });
              i++;
            }
            return items;
          }
        }
    
        if (body instanceof FormData) {
          const ids = body.getAll('id');
          if (ids.length) {
            return ids.map(id => ({ id: String(id), quantity: 1 }));
          }
          const items = [];
          let i = 0;
          while (body.has(`items[${i}][id]`)) {
            const id = body.get(`items[${i}][id]`);
            const qty = parseInt(body.get(`items[${i}][quantity]`) || 1, 10);
            if (id) items.push({ id: String(id), quantity: qty });
            i++;
          }
          return items;
        }
    
        if (body && typeof body === 'object') {
          if (body.id) {
            return [{ id: String(body.id), quantity: parseInt(body.quantity, 10) || 1 }];
          }
          if (body.items && Array.isArray(body.items)) {
            return body.items.map(item => ({
              id: String(item.id),
              quantity: parseInt(item.quantity, 10) || 1
            }));
          }
        }
    
        return [];
      }
    
      XMLHttpRequest.prototype.open = function(method, url, ...args) {
        this._method = method;
        this._url = url;
        return originalOpen.apply(this, [method, url, ...args]);
      };
    
      XMLHttpRequest.prototype.send = function(body) {
        const isCartAdd = /\/cart\/add(\.js)?(\?|$)/.test(this._url);
        const isPost = this._method && this._method.toUpperCase() === 'POST';
    
        if (isCartAdd && isPost) {
          const items = extractItemsFromBody(body);
          
          if (items.length === 0) {
            return originalSend.apply(this, [body]);
          }
    
          const allowedItems = items.filter(item => !BOUTIQUE_VARIANT_IDS.has(item.id));
    
          if (allowedItems.length === 0) {
            this.status = 200;
            this.responseText = JSON.stringify({ status: 200, message: 'No items to add' });
            if (this.onload) this.onload();
            return;
          }
    
          if (allowedItems.length !== items.length) {
    
            let newBody;
            if (allowedItems.length === 1) {
              newBody = JSON.stringify({ id: allowedItems[0].id, quantity: allowedItems[0].quantity });
            } else {
              newBody = JSON.stringify({ items: allowedItems });
            }
    
            body = newBody;
            try {
              this.setRequestHeader('Content-Type', 'application/json');
            } catch (e) {}
          }
        }
    
        return originalSend.apply(this, [body]);
      };
    }

  async function removeBoutiqueItemsFromCart(showMessage = true) {
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      const boutiqueItems = cart.items.filter(item =>
        BOUTIQUE_VARIANT_IDS.has(String(item.variant_id))
      );

      if (boutiqueItems.length === 0) return;

      const updates = {};
      boutiqueItems.forEach(item => {
        updates[item.key] = 0;
      });

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
  
      if (showMessage) 
        alert('Alcuni articoli Boutique Exclusive sono stati rimossi dal carrello perché acquistabili solo in negozio.');
      if (window.location.pathname.includes('/cart'))
        window.location.reload();
      
      document.dispatchEvent(new Event('cart:updated'));
    } catch (error) {
      console.error('Errore durante la rimozione dei prodotti boutique dal carrello:', error);
    }
  }

  /**
   * Installs the cart/add fetch guard once.
   */
  function installCartAddGuard() {
    if (window.__boutiqueOriginalFetch) return;
    window.__boutiqueOriginalFetch = window.fetch.bind(window);
    window.fetch = guardedFetch;
  }

  document.addEventListener('DOMContentLoaded', () => {
    seedBoutiqueVariantsFromDom();
    installCartAddGuard();
    installXHRInterceptor();
    observeWishlist();

    if(window.location.pathname.includes('/cart')) {
        removeBoutiqueItemsFromCart(false);
    }

    document.addEventListener('cart:updated', () => {
        if (window.location.pathname.includes('/cart')) {
            removeBoutiqueItemsFromCart(false);
        }
    });
  });
})();
